import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate } from '@/lib/utils'
import { marginFromSnapshot, formatMarginPct, type SnapshotMarginInput } from '@/lib/margin'
import { ArrowLeft, Trash2, Send, CheckCircle2, X, DollarSign, Repeat, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import LineItemEditor, { type LineItem } from '@/components/payments/LineItemEditor'
import LinkContactModal from '@/components/payments/LinkContactModal'
import TotalsPanel from '@/components/payments/TotalsPanel'
import { renderInvoiceEmail } from '@/lib/invoiceEmail'
import { renderReceiptEmail } from '@/lib/receiptEmail'
import { sendEmail } from '@/lib/email'
import { computeTotals, type DiscountType } from '@/lib/totals'
import type { Frequency } from '@/lib/recurringInvoiceCadence'
import CadencePicker, { type EndCondition } from '@/components/payments/CadencePicker'

type PaymentMethod = 'cash' | 'check' | 'card' | 'ach' | 'other'

const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash', check: 'Check', card: 'Card', ach: 'ACH', other: 'Other',
}

// Sentinel UUID written by NewInvoiceModal's manual-name fallback when the
// operator types a customer name instead of picking from the contacts
// dropdown. invoices.contact_id accepts it, but recurring_invoice_series's
// FK to contacts(id) doesn't — converting such an invoice to a series
// must be guarded. NewInvoiceModal (Invoices.tsx:50) currently writes the
// literal directly; banked to import this constant from there too.
const UNBOUND_CONTACT_ID = '00000000-0000-0000-0000-000000000000'

interface InvoicePayment {
  id: string
  invoice_id: string
  workspace_id: string
  amount: number
  method: PaymentMethod
  reference: string | null
  notes: string | null
  paid_at: string
  recorded_by: string | null
  created_at: string
  // PostgREST embed via FK invoice_payments_recorded_by_fkey.
  // profiles has a single `full_name` column (not first/last) — confirmed live.
  recorder: { full_name: string | null } | null
}

function RecordPaymentModal({
  invoice, defaultMethod, modalTitle, onClose, onSubmit,
}: {
  invoice: any
  defaultMethod: PaymentMethod
  modalTitle: string
  onClose: () => void
  onSubmit: (form: {
    amount: number; method: PaymentMethod; reference: string; paidAt: string; notes: string;
    emailReceipt: boolean;
  }) => Promise<void>
}) {
  const amountDue = Number(invoice.amount_due) || 0
  const total = Number(invoice.total) || 0
  const contactEmail = (invoice.contact_email || '').trim()
  const canEmail = contactEmail.length > 0
  const [amount, setAmount] = useState(amountDue > 0 ? amountDue.toFixed(2) : total.toFixed(2))
  const [method, setMethod] = useState<PaymentMethod>(defaultMethod)
  const [reference, setReference] = useState('')
  const [paidAt, setPaidAt] = useState(new Date().toISOString().split('T')[0])
  const [notes, setNotes] = useState('')
  // Receipt opt-in defaults ON when there's an email on file. Forced OFF
  // when there isn't (no recipient to send to).
  const [emailReceipt, setEmailReceipt] = useState(canEmail)
  const [saving, setSaving] = useState(false)

  const amountNum = parseFloat(amount) || 0
  const isOverpayment = amountNum > amountDue && amountDue > 0
  const isInvalid = !(amountNum > 0)

  const handleSubmit = async () => {
    if (isInvalid) { toast.error('Amount must be greater than zero'); return }
    setSaving(true)
    try {
      await onSubmit({ amount: amountNum, method, reference, paidAt, notes, emailReceipt: emailReceipt && canEmail })
    } finally {
      setSaving(false)
    }
  }

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{modalTitle}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ padding: '10px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 9, fontSize: 12, color: 'var(--ink-secondary)' }}>
            Balance due: <strong style={{ color: 'var(--ink-primary)' }}>{formatCurrency(amountDue)}</strong> of {formatCurrency(total)} total
          </div>

          <div>
            <label style={lbl}>Amount *</label>
            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={inp} autoFocus
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            {isOverpayment && (
              <p style={{ fontSize: 11, color: 'var(--warning)', margin: '6px 0 0' }}>This exceeds the balance due — overpayment will be recorded.</p>
            )}
          </div>

          <div>
            <label style={lbl}>Method</label>
            <select value={method} onChange={e => setMethod(e.target.value as PaymentMethod)} style={{ ...inp, cursor: 'pointer' }}>
              {(['cash', 'check', 'card', 'ach', 'other'] as PaymentMethod[]).map(m => (
                <option key={m} value={m}>{METHOD_LABELS[m]}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={lbl}>Reference</label>
            <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Check #, transaction ID…" style={inp}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>

          <div>
            <label style={lbl}>Date</label>
            {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
            <input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} style={{ ...inp, colorScheme: 'dark' }}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>

          <div>
            <label style={lbl}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes…" rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>

          {/* Receipt opt-in. Disabled (and the checkbox forced off) when the
              invoice has no contact_email — there's nothing to send to. */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
            background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 9,
            cursor: canEmail ? 'pointer' : 'not-allowed',
            opacity: canEmail ? 1 : 0.6,
          }}>
            <input type="checkbox"
              checked={emailReceipt && canEmail}
              disabled={!canEmail}
              onChange={e => setEmailReceipt(e.target.checked)}
              style={{ marginTop: 2, cursor: canEmail ? 'pointer' : 'not-allowed' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>Email receipt to customer</p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                {canEmail
                  ? `Sends to ${contactEmail} after the payment is recorded.`
                  : 'No email on file for this customer.'}
              </p>
            </div>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || isInvalid} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Recording...' : 'Record payment'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Used only by MakeRecurringModal's title auto-default (defaultTitleFor).
// The cadence select itself reads from CadencePicker's own internal list.
const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly',    label: 'Yearly' },
]

function MakeRecurringModal({
  invoice, onClose, onCreated,
}: {
  invoice: any
  onClose: () => void
  onCreated: (seriesId: string) => void
}) {
  const { workspace } = useAuth()
  const today = new Date().toISOString().split('T')[0]
  const defaultTitleFor = (freq: Frequency) =>
    `${invoice.contact_name || 'Customer'} — ${FREQUENCY_OPTIONS.find(o => o.value === freq)?.label || 'Monthly'}`

  const [frequency, setFrequency] = useState<Frequency>('monthly')
  const [title, setTitle] = useState(defaultTitleFor('monthly'))
  const [titleTouched, setTitleTouched] = useState(false)
  const [startDate, setStartDate] = useState(today)
  const [endCondition, setEndCondition] = useState<EndCondition>('never')
  const [endDate, setEndDate] = useState('')
  const [endAfterCount, setEndAfterCount] = useState('12')
  const [sendMode, setSendMode] = useState<'draft' | 'auto_send'>('draft')
  const [saving, setSaving] = useState(false)

  // Keep the title's default fresh when frequency changes — but only if the
  // user hasn't typed their own value. The moment they edit, we stop
  // overwriting (titleTouched flag).
  const onFrequencyChange = (next: Frequency) => {
    setFrequency(next)
    if (!titleTouched) setTitle(defaultTitleFor(next))
  }

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }

  const handleSubmit = async () => {
    // Belt-and-suspenders for the action-bar gate's contact check: catches
    // the edge case of a deleted contact (no such rows in dev today, but
    // possible after future cleanup) where the button might slip past
    // the hasValidContact gate. Surfaces a clear message instead of the
    // raw FK violation from recurring_invoice_series_contact_id_fkey.
    if (!invoice.contact_id || invoice.contact_id === UNBOUND_CONTACT_ID) {
      toast.error("This invoice isn't linked to a saved contact. Add the customer in Contacts first to set up recurring billing.")
      return
    }
    if (!title.trim()) { toast.error('Schedule needs a title'); return }
    if (!startDate)    { toast.error('Pick a start date'); return }
    if (endCondition === 'on_date' && !endDate)       { toast.error('Pick an end date'); return }
    if (endCondition === 'after_count') {
      const n = parseInt(endAfterCount, 10)
      if (!Number.isFinite(n) || n < 1) { toast.error('After count must be at least 1'); return }
    }

    // Build the template from the invoice. Generator tolerates missing keys
    // (only line_items + contact info are load-bearing). payment_terms
    // defaults to net_30 if absent so the BEFORE-INSERT due-date trigger
    // has a value to derive from.
    const template: Record<string, any> = {
      line_items: invoice.line_items || [],
      contact_name: invoice.contact_name || '',
      contact_email: invoice.contact_email || '',
      company_name: invoice.company_name || null,
      payment_terms: invoice.payment_terms || 'net_30',
      discount_type: invoice.discount_type || 'none',
      discount_value: invoice.discount_value ?? 0,
      tax_rate: invoice.tax_rate ?? 0,
      notes: invoice.notes ?? null,
      footer_text: invoice.footer_text ?? null,
      service_line_id: invoice.service_line_id ?? null,
      currency: invoice.currency || 'USD',
      // Billing presentation CP5: the new series inherits the source invoice's
      // amount-only presentation; generated children carry it via the template.
      display_amount_only: !!invoice.display_amount_only,
    }

    setSaving(true)
    const { data, error } = await supabase
      .from('recurring_invoice_series')
      .insert({
        workspace_id: workspace!.id,
        contact_id: invoice.contact_id,
        company_id: invoice.company_id ?? null,
        service_line_id: invoice.service_line_id ?? null,
        title: title.trim(),
        template,
        frequency,
        start_date: startDate,
        next_run_date: startDate, // first generation = start_date
        end_date: endCondition === 'on_date' ? endDate : null,
        end_after_count: endCondition === 'after_count' ? parseInt(endAfterCount, 10) : null,
        send_mode: sendMode,
        status: 'active',
        generated_count: 0,
      })
      .select('id')
      .single()
    setSaving(false)

    if (error) { toast.error(`Couldn't create schedule: ${error.message}`); return }
    onCreated(data!.id)
    onClose()
  }

  const startIsToday = startDate === today
  const startIsPast  = startDate !== '' && startDate < today

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Repeat style={{ width: 16, height: 16, color: 'var(--accent)' }} />
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Make recurring</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ padding: '10px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 9, fontSize: 12, color: 'var(--ink-secondary)' }}>
            Copies the line items, contact, and totals from <strong style={{ color: 'var(--ink-primary)' }}>{invoice.invoice_number}</strong> and generates a new draft invoice on the cadence below.
          </div>

          <div>
            <label style={lbl}>Schedule title</label>
            <input value={title}
              onChange={e => { setTitle(e.target.value); setTitleTouched(true) }}
              style={inp}
              onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>

          <CadencePicker
            frequency={frequency}
            startDate={startDate}
            endCondition={endCondition}
            endDate={endDate}
            endAfterCount={endAfterCount}
            sendMode={sendMode}
            onFrequencyChange={onFrequencyChange}
            onStartDateChange={setStartDate}
            onEndConditionChange={setEndCondition}
            onEndDateChange={setEndDate}
            onEndAfterCountChange={setEndAfterCount}
            onSendModeChange={setSendMode}
            previewHeader="first"
            showSweepNote={startIsToday || startIsPast}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Creating...' : 'Create schedule'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function InvoiceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace, user } = useAuth()
  const queryClient = useQueryClient()
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [taxRate, setTaxRate] = useState(0)
  const [discountType, setDiscountType] = useState<DiscountType>('none')
  const [discountValue, setDiscountValue] = useState(0)
  // Arc B Slice 1: invoice-level minimum charge (null = none). Floors the subtotal.
  const [minimumCharge, setMinimumCharge] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  // Billing presentation CP1: amount-only display flag. Persists alongside the
  // other presentation fields (notes/minimum_charge) via every save path below.
  const [displayAmountOnly, setDisplayAmountOnly] = useState(false)
  const [saving, setSaving] = useState(false)
  // Two entry points open the same modal — only the default method differs.
  // Null = closed. 'check' = Record payment button. 'other' = Mark paid sugar.
  const [paymentModalDefault, setPaymentModalDefault] = useState<{ method: PaymentMethod; title: string } | null>(null)
  const [showMakeRecurringModal, setShowMakeRecurringModal] = useState(false)
  const [showLinkContact, setShowLinkContact] = useState(false)

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('invoices').select('*, recurring_invoice_series(title)').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  // Field jobs this invoice covers. Empty for hand-typed invoices; one row for
  // per-job convert; 2+ for consolidated invoices created from BillingTab.
  // Render rule below shows the section only at 2+ so per-job invoices stay
  // visually unchanged (the count===1 case would be noise).
  const { data: coveredJobs } = useQuery<
    { id: string; job_number: string | null; scheduled_date: string | null; title: string | null }[]
  >({
    queryKey: ['invoice-covered-jobs', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_jobs')
        .select('id, job_number, scheduled_date, title')
        .eq('invoice_id', id!)
        .order('scheduled_date', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    enabled: !!id,
  })

  // Slice 4: the frozen cost basis for this invoice (one row, keyed invoice_id),
  // written at job→invoice conversion. Staff-only — invoice_cost_snapshots RLS
  // (workspace_isolation, no portal/vendor) blocks any non-staff read. Cast the
  // client: the new table isn't in the generated Database types yet (as CP2's
  // helper does). Margin is computed ON READ from these frozen primitives.
  const { data: costSnapshot } = useQuery<SnapshotMarginInput | null>({
    queryKey: ['invoice-cost-snapshot', id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('invoice_cost_snapshots')
        .select('*')
        .eq('invoice_id', id!)
        .maybeSingle()
      return (data as SnapshotMarginInput) ?? null
    },
    enabled: !!id,
  })

  // The payment ledger for this invoice. The reconcile trigger keeps
  // invoices.amount_paid / amount_due / status / paid_at in sync — we just
  // INSERT / DELETE rows here and refetch.
  // Embed via FK invoice_payments_recorded_by_fkey using the column-name hint
  // `!recorded_by`. profiles exposes a single `full_name` text column.
  const { data: payments = [], error: paymentsError } = useQuery<InvoicePayment[]>({
    queryKey: ['invoice-payments', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_payments')
        .select('*, recorder:profiles!recorded_by(full_name)')
        .eq('invoice_id', id!)
        .order('paid_at', { ascending: false })
      if (error) {
        // Surface the precise PostgREST error so a broken query can't
        // silently masquerade as zero payments again (see also the inline
        // error message rendered in the Payment history section below).
        console.error('[invoice-payments] query failed', error)
        throw error
      }
      return (data as any[]) ?? []
    },
    enabled: !!id,
  })

  useEffect(() => {
    if (invoice) {
      setLineItems(invoice.line_items || [])
      setTaxRate(invoice.tax_rate || 0)
      // Cast: see QuoteDetail comment — CHECK constraint guarantees the value.
      setDiscountType((invoice.discount_type as DiscountType) || 'none')
      setDiscountValue(invoice.discount_value || 0)
      setMinimumCharge(invoice?.minimum_charge ?? null)
      setNotes(invoice.notes || '')
      setDisplayAmountOnly(!!invoice.display_amount_only)
    }
  }, [invoice?.id])

  const totals = useCallback(() => {
    // Arc B Slice 1: floor the subtotal by the invoice's minimum_charge (null =
    // none). Cast — minimum_charge isn't in the generated types yet. The floored
    // totals get persisted, so the portal (stored-column reader) shows them.
    const { subtotal, discountAmount, taxAmount, total } = computeTotals({
      lineItems, discountType, discountValue, taxRate,
      minimumCharge,
    })
    return { subtotal, discountAmount, taxAmount, total }
  }, [lineItems, discountType, discountValue, taxRate, minimumCharge])

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('invoices').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: () => toast.error('Failed to save'),
  })

  const saveLineItems = async () => {
    setSaving(true)
    const { subtotal, discountAmount, taxAmount, total } = totals()
    await updateMutation.mutateAsync({
      line_items: lineItems,
      subtotal,
      discount_type: discountType,
      discount_value: discountValue,
      discount_amount: discountAmount,
      minimum_charge: minimumCharge,
      display_amount_only: displayAmountOnly,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total,
      amount_due: total - (invoice?.amount_paid || 0),
      notes,
    })
    setSaving(false)
    toast.success('Invoice saved')
  }

  const markSentAndEmail = async () => {
    const { subtotal, discountAmount, taxAmount, total } = totals()
    const newAmountDue = total - (invoice?.amount_paid || 0)

    // Step 1: status update + persist current state.
    try {
      await updateMutation.mutateAsync({
        status: 'sent',
        sent_at: new Date().toISOString(),
        line_items: lineItems,
        subtotal,
        discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
        tax_rate: taxRate, tax_amount: taxAmount, total,
        amount_due: newAmountDue,
        notes,
        display_amount_only: displayAmountOnly,
      })
    } catch (err) {
      toast.error('Failed to save invoice — not sent')
      return
    }

    // Step 2: render and send the email.
    const recipientEmail = invoice?.contact_email
    if (!recipientEmail) {
      toast.warning('Invoice saved as sent, but no email address on file — nothing was emailed.')
      return
    }

    try {
      const { subject, html } = renderInvoiceEmail({
        invoiceNumber: invoice.invoice_number,
        contactName: invoice.contact_name || 'there',
        workspaceName: workspace?.name || 'Orkestra',
        lineItems: lineItems.map(li => ({
          name: li.name,
          description: li.description,
          qty: li.qty,
          unit_price: li.unit_price,
        })),
        currency: invoice.currency || 'USD',
        subtotal,
        taxRate: taxRate,
        taxAmount,
        total,
        discountAmount,
        amountPaid: invoice.amount_paid,
        amountDue: newAmountDue,
        dueDate: invoice.due_date,
        issueDate: invoice.issue_date,
        notes,
        displayAmountOnly,
        brandColor: workspace?.brand_color || undefined,
        logoUrl: workspace?.logo_url || undefined,
      })

      const { error } = await sendEmail({
        to: recipientEmail,
        subject,
        html,
        replyTo: user?.email || undefined,
      })

      if (error) {
        toast.error(`Invoice saved, but email failed: ${error.message}`)
      } else {
        toast.success(`Invoice sent to ${recipientEmail}`)
      }
    } catch (err) {
      toast.error(`Invoice saved, but email failed: ${err instanceof Error ? err.message : 'unknown error'}`)
    }
  }

  // Mark paid is now sugar: opens the payment modal pre-filled with the
  // full balance, default method 'other' (the "just mark it paid" intent —
  // operator isn't tracking a specific method). The submit handler
  // (handlePaymentSubmit) persists any pending line-item edits FIRST,
  // then INSERTs the payment so the reconcile trigger sees fresh totals.
  const openMarkPaid = () => setPaymentModalDefault({ method: 'other', title: 'Mark invoice paid' })
  const openRecordPayment = () => setPaymentModalDefault({ method: 'check', title: 'Record payment' })

  const handlePaymentSubmit = async (form: {
    amount: number; method: PaymentMethod; reference: string; paidAt: string; notes: string;
    emailReceipt: boolean;
  }) => {
    if (!invoice) return
    // Step 1: persist pending line-item / discount / tax / notes edits so the
    // trigger reconciles against the latest invoice.total.
    const { subtotal, discountAmount, taxAmount, total } = totals()
    const currentPaid = Number(invoice.amount_paid) || 0
    try {
      await updateMutation.mutateAsync({
        line_items: lineItems,
        subtotal,
        discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
        tax_rate: taxRate, tax_amount: taxAmount, total,
        amount_due: Math.max(total - currentPaid, 0),
        notes,
        display_amount_only: displayAmountOnly,
      })
    } catch (err) {
      toast.error('Failed to save invoice edits — payment not recorded')
      return
    }

    // Step 2: INSERT the payment row. The trigger then UPDATEs the parent
    // invoice's amount_paid / amount_due / status / paid_at.
    const paidAtIso = new Date(form.paidAt + 'T12:00:00Z').toISOString()
    const { error: insertErr } = await supabase.from('invoice_payments').insert({
      workspace_id: invoice.workspace_id,
      invoice_id: invoice.id,
      amount: form.amount,
      method: form.method,
      reference: form.reference.trim() || null,
      notes: form.notes.trim() || null,
      paid_at: paidAtIso,
      recorded_by: user?.id || null,
    })
    if (insertErr) {
      // Surface the trigger's RAISE (void block) or RLS rejection cleanly.
      toast.error(`Failed to record payment: ${insertErr.message}`)
      return
    }

    // Step 3: AUTHORITATIVE read of the post-reconcile invoice state. The
    // trigger has UPDATEd amount_due / status synchronously inside the
    // INSERT's transaction; this fetch returns those fresh values without
    // racing the React Query refetch (which would have to wait for
    // invalidateQueries to schedule + the subscription to fire). This is
    // also race-safe against concurrent payments on the same invoice (the
    // trigger's FOR UPDATE serializes them; our select reads the result).
    const { data: freshInvoice, error: freshErr } = await supabase
      .from('invoices')
      .select('amount_due, status, contact_email, contact_name, invoice_number, total, currency')
      .eq('id', invoice.id)
      .single()
    const remaining = freshErr
      ? Math.max(total - (currentPaid + form.amount), 0)  // fallback if fresh read fails
      : Math.max(Number(freshInvoice?.amount_due) || 0, 0)

    // Step 4: kick off React Query invalidations (the UI updates in the
    // background while we move on to the receipt send).
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['invoice', id] }),
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', id] }),
      queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    ])

    // Step 5: best-effort receipt send. The payment is ALREADY recorded —
    // a send failure does not roll it back. Caller's intent (emailReceipt)
    // and a recipient on file are both required.
    const recipient = (freshInvoice?.contact_email || invoice.contact_email || '').trim()
    const wantsReceipt = form.emailReceipt && recipient.length > 0
    let receiptError: string | null = null
    if (wantsReceipt) {
      try {
        const { subject, html } = renderReceiptEmail({
          invoiceNumber: freshInvoice?.invoice_number || invoice.invoice_number,
          contactName: freshInvoice?.contact_name || invoice.contact_name || 'there',
          workspaceName: workspace?.name || 'Orkestra',
          currency: freshInvoice?.currency || invoice.currency || 'USD',
          invoiceTotal: Number(freshInvoice?.total) || total,
          paymentAmount: form.amount,
          paymentMethod: form.method,
          paymentDate: paidAtIso,
          paymentReference: form.reference.trim() || null,
          remainingBalance: remaining,
          brandColor: workspace?.brand_color || undefined,
          logoUrl: workspace?.logo_url || undefined,
        })
        const { error: sendErr } = await sendEmail({
          to: recipient,
          subject,
          html,
          replyTo: user?.email || undefined,
        })
        if (sendErr) {
          receiptError = sendErr.message
          console.error('[receipt-email] send failed', sendErr)
        }
      } catch (err) {
        receiptError = err instanceof Error ? err.message : 'unknown error'
        console.error('[receipt-email] render or send threw', err)
      }
    }

    // Step 6: single toast capturing payment + (optional) receipt outcome.
    const balanceText = remaining === 0
      ? 'invoice is now paid'
      : `${formatCurrency(remaining)} remaining`
    if (wantsReceipt && receiptError) {
      toast.warning(`Payment recorded — ${balanceText} · receipt email failed`)
    } else if (wantsReceipt) {
      toast.success(`Payment recorded — ${balanceText} · receipt sent`)
    } else {
      toast.success(`Payment recorded — ${balanceText}`)
    }
    setPaymentModalDefault(null)
  }

  const deletePayment = async (paymentId: string) => {
    if (!window.confirm("Remove this payment? The invoice's balance and status will be recalculated.")) return
    const { error } = await supabase.from('invoice_payments').delete().eq('id', paymentId)
    if (error) { toast.error(`Failed to remove payment: ${error.message}`); return }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['invoice', id] }),
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', id] }),
      queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    ])
    toast.success('Payment removed')
  }

  const voidInvoice = async () => {
    if (!window.confirm('Void this invoice?')) return
    const { subtotal, discountAmount, taxAmount, total } = totals()
    await updateMutation.mutateAsync({
      status: 'void',
      display_amount_only: displayAmountOnly,
      line_items: lineItems,
      subtotal,
      discount_type: discountType, discount_value: discountValue, discount_amount: discountAmount, minimum_charge: minimumCharge,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total,
      amount_due: total - (invoice?.amount_paid || 0),
      notes,
    })
    toast.success('Invoice voided')
  }

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (!invoice) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-secondary)' }}>Invoice not found</div>

  const { subtotal, discountAmount, taxAmount, total } = totals()
  const isPaid = invoice.status === 'paid'
  const isVoid = invoice.status === 'void'
  const isLocked = isPaid || isVoid
  // Make recurring needs a real contacts(id) — the series FK rejects the
  // sentinel UUID written by NewInvoiceModal's manual-name fallback.
  const hasValidContact = !!invoice.contact_id && invoice.contact_id !== UNBOUND_CONTACT_ID

  // Provenance badge: sweep-generated invoices carry a FK to their series.
  // The recurring_invoice_series(title) embed gives the schedule's title.
  // The FK is ON DELETE SET NULL, so a deleted series nulls the id and the
  // badge disappears on its own. When the id is set but the title embed is
  // absent (RLS edge / mid-delete race), we still show a non-clickable badge
  // rather than a blank "Generated from ".
  const seriesId: string | null = invoice.recurring_invoice_series_id ?? null
  const seriesTitle: string | null = invoice.recurring_invoice_series?.title ?? null

  const STATUS_COLORS: Record<string, string> = {
    draft:   'var(--ink-secondary)',
    sent:    'var(--info)',
    viewed:  'var(--accent-text)',
    paid:    'var(--positive)',
    partial: 'var(--warning)',
    overdue: 'var(--danger)',
    void:    'var(--ink-tertiary)',
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/invoices') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', fontFamily: 'monospace' }}>{invoice.invoice_number}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: `color-mix(in srgb, ${STATUS_COLORS[invoice.status]} var(--status-tint-fill), transparent)`, color: STATUS_COLORS[invoice.status], border: `1px solid color-mix(in srgb, ${STATUS_COLORS[invoice.status]} var(--status-tint-strong), transparent)`, textTransform: 'capitalize' }}>
          {invoice.status}
        </span>

        {/* Provenance badge — shown only for sweep-generated invoices (FK set).
            Clickable when we have the series title (jumps to the schedule);
            non-clickable fallback when the title embed is missing but the FK
            is still set. Accent pill, mirrors the status-pill recipe. */}
        {seriesId && (
          seriesTitle ? (
            <button onClick={() => navigate('/app/invoices/recurring/' + seriesId)}
              title="View recurring schedule" aria-label="View recurring schedule"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 280, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)', cursor: 'pointer' }}>
              <Repeat style={{ width: 11, height: 11, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Generated from {seriesTitle}</span>
            </button>
          ) : (
            <span title="Generated by a recurring schedule"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
              <Repeat style={{ width: 11, height: 11, flexShrink: 0 }} />
              Generated from a recurring schedule
            </span>
          )
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isLocked && (
            <>
              <button onClick={saveLineItems} disabled={saving}
                style={{ padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {invoice.status === 'draft' && (
                <button onClick={markSentAndEmail} className="btn-primary" style={{ padding: '7px 14px', fontSize: 12 }}>
                  <Send style={{ width: 13, height: 13 }} /> Send
                </button>
              )}
              {invoice.amount_due > 0 && (
                <button onClick={openRecordPayment}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <DollarSign style={{ width: 13, height: 13 }} /> Record payment
                </button>
              )}
              {['sent', 'viewed', 'partial'].includes(invoice.status) && (
                <button onClick={openMarkPaid}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <CheckCircle2 style={{ width: 13, height: 13 }} /> Mark paid
                </button>
              )}
            </>
          )}
          {/* Make recurring — hidden once an invoice already belongs to a
              series (no double-conversion) and on void invoices. Available
              on all other statuses (incl. paid, since converting a paid
              invoice to a schedule is a sensible flow: it's the proof the
              cadence works). When the invoice's contact_id is the sentinel
              (NewInvoiceModal manual-name fallback), the button renders as
              a disabled affordance with a tooltip — the operator sees what
              the action is + learns why it can't run, rather than hitting
              a cryptic FK violation on submit. */}
          {!isVoid && !invoice.recurring_invoice_series_id && (
            hasValidContact ? (
              <button onClick={() => setShowMakeRecurringModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Repeat style={{ width: 13, height: 13 }} /> Make recurring
              </button>
            ) : (
              <button disabled
                title="Recurring billing needs a saved contact. Add the customer in Contacts first."
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'not-allowed', opacity: 0.5 }}>
                <Repeat style={{ width: 13, height: 13 }} /> Make recurring
              </button>
            )
          )}
          {!isVoid && !isPaid && (
            <button onClick={voidInvoice}
              style={{ padding: '7px 14px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Void
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>

          {/* Invoice header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 36 }}>
            <div>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, boxShadow: 'var(--logo-glow)' }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--accent-ink)' }}>O</span>
              </div>
              <p style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Orkestra</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{workspace?.name}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 28, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 6px', letterSpacing: '-0.03em', fontFamily: 'monospace' }}>{invoice.invoice_number}</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 4px' }}>Issued {formatDate(invoice.issue_date)}</p>
              {invoice.due_date && <p style={{ fontSize: 13, color: invoice.status === 'overdue' ? 'var(--danger)' : 'var(--ink-tertiary)', margin: 0 }}>Due {formatDate(invoice.due_date)}</p>}
            </div>
          </div>

          {/* Bill to */}
          <div style={{ marginBottom: 32, padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Bill to</p>
              {/* Repair affordance for legacy unbound invoices (sentinel or NULL
                  contact_id). Independent of isLocked — a paid/void unbound
                  invoice still needs to be linkable. Disappears once bound. */}
              {!hasValidContact && (
                <button onClick={() => setShowLinkContact(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <UserPlus style={{ width: 13, height: 13 }} /> Link customer
                </button>
              )}
            </div>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{invoice.contact_name}</p>
            {invoice.company_name && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{invoice.company_name}</p>}
            {invoice.contact_email && <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{invoice.contact_email}</p>}
          </div>

          {/* Covers visits — consolidated invoices only (≥2 covered jobs). */}
          {coveredJobs && coveredJobs.length >= 2 && (
            <div style={{ marginBottom: 32, padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Covers {coveredJobs.length} visits</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {coveredJobs.map(j => (
                  <div key={j.id} style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 13 }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--ink-tertiary)', minWidth: 84 }}>{j.job_number ?? '—'}</span>
                    <span style={{ color: 'var(--ink-secondary)', minWidth: 100 }}>{j.scheduled_date ? formatDate(j.scheduled_date) : '—'}</span>
                    <span style={{ color: 'var(--ink-primary)' }}>{j.title || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Line items */}
          <LineItemEditor
            items={lineItems}
            onItemsChange={setLineItems}
            isLocked={isLocked}
            workspaceId={workspace!.id}
          />

          {/* Totals */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginBottom: 24 }}>
            <TotalsPanel
              subtotal={subtotal}
              discountType={discountType}
              discountValue={discountValue}
              discountAmount={discountAmount}
              onDiscountTypeChange={setDiscountType}
              onDiscountValueChange={setDiscountValue}
              taxRate={taxRate}
              taxAmount={taxAmount}
              onTaxRateChange={setTaxRate}
              total={total}
              currency={invoice.currency || 'USD'}
              isLocked={isLocked}
              amountPaid={invoice.amount_paid}
              amountDue={invoice.amount_due}
            />
            {/* Arc B Slice 1: minimum-charge input — sits below TotalsPanel (the
                shared customer-facing component stays untouched). Floors the
                subtotal; the totals above already reflect it live. Empty = none. */}
            <div style={{ width: 280, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Minimum charge</span>
              {isLocked ? (
                <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>
                  {minimumCharge != null ? formatCurrency(minimumCharge, invoice.currency || 'USD') : '—'}
                </span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>$</span>
                  <input type="number" min={0} step={0.01}
                    value={minimumCharge ?? ''}
                    onChange={e => { const v = parseFloat(e.target.value); setMinimumCharge(Number.isFinite(v) && v > 0 ? v : null) }}
                    placeholder="None"
                    style={{ width: 72, padding: '2px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 5, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', textAlign: 'right' }} />
                </div>
              )}
            </div>
            {/* Billing presentation CP1: amount-only display toggle. Persists via
                the same save paths as minimum_charge/notes (Save / Send / payment /
                void). Presentation-only — the staff editor above always shows
                qty/rate; the customer-facing renders (email/portal/PDF, later CPs)
                read this flag. isLocked mirrors minimum_charge's read-only treatment. */}
            <div style={{ width: 280, marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Amount-only display</span>
                {isLocked ? (
                  <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>{displayAmountOnly ? 'On' : 'Off'}</span>
                ) : (
                  <div role="switch" aria-checked={displayAmountOnly} tabIndex={0}
                    onClick={() => setDisplayAmountOnly(v => !v)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisplayAmountOnly(v => !v) } }}
                    style={{ width: 36, height: 20, borderRadius: 100, background: displayAmountOnly ? 'var(--accent)' : 'var(--border-default)', transition: 'background 200ms ease', position: 'relative', flexShrink: 0, cursor: 'pointer' }}>
                    <div style={{ position: 'absolute', top: 3, left: displayAmountOnly ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', transition: 'left 200ms ease', boxShadow: 'var(--shadow-knob)' }} />
                  </div>
                )}
              </div>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0', lineHeight: 1.5 }}>
                Hide quantities and rates on the customer's invoice. Totals are unchanged.
              </p>
              {displayAmountOnly && (
                <p style={{ fontSize: 11, color: 'var(--accent-text)', margin: '4px 0 0', lineHeight: 1.5 }}>
                  Customers will see line amounts only.
                </p>
              )}
            </div>
            {/* Slice 4: staff-only FROZEN margin — internal, COMPUTED-ON-READ from
                the conversion-time snapshot, never shown to customers/vendors.
                Shown only for job-sourced invoices (coveredJobs>0) that have a
                snapshot. Mirrors the JobLineItemsEditor live-margin treatment;
                "partial" when line costs or a subbed job's vendor bill were
                incomplete at conversion, so it never reads as precise. */}
            {(coveredJobs?.length ?? 0) > 0 && costSnapshot && (() => {
              const fm = marginFromSnapshot(costSnapshot)
              return (
                <div title="Internal gross margin: line revenue − (line cost + vendor + labor cost), frozen at conversion. Never shown to customers or vendors."
                  style={{ width: 280, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)', fontStyle: 'italic' }}>
                    {!fm.showable
                      ? 'Frozen margin (internal — no costs entered)'
                      : fm.isPartial
                        ? `Frozen margin (partial — ${fm.partialReason})`
                        : 'Frozen margin (internal)'}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)', fontStyle: 'italic', fontVariantNumeric: 'tabular-nums' }}>
                    {!fm.showable ? '—' : `${formatCurrency(fm.margin, invoice.currency || 'USD')} · ${formatMarginPct(fm.marginPct)}`}
                  </span>
                </div>
              )
            })()}
          </div>

          {/* Payment history — shown when there's a ledger entry, any
              amount paid, OR the query errored (so a broken query is
              VISIBLE instead of silently empty). Delete affordance remains
              available on void (the trigger permits DELETE on void for
              error-correction); only the Record / Mark-paid buttons are
              gated above. */}
          {(payments.length > 0 || (Number(invoice.amount_paid) || 0) > 0 || paymentsError) && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Payment history</h3>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                  {payments.length} {payments.length === 1 ? 'payment' : 'payments'}
                </span>
              </div>
              {paymentsError ? (
                <div style={{ padding: '12px 16px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid var(--danger-border)', borderRadius: 12, fontSize: 12, color: 'var(--danger)' }}>
                  Couldn't load payment history — {(paymentsError as any)?.message || 'unknown error'}. The amount paid on this invoice is still correct (it's derived from the ledger by the database).
                </div>
              ) : (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
                  {payments.map((p, i) => {
                    const recorderName = (p.recorder?.full_name || '').trim() || '—'
                    return (
                      <div key={p.id} style={{
                        display: 'grid',
                        gridTemplateColumns: '110px 1fr 130px 100px 36px',
                        gap: 12,
                        padding: '12px 16px',
                        borderBottom: i < payments.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                        alignItems: 'center',
                      }}>
                        <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{formatDate(p.paid_at, 'MMM d, yyyy')}</span>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>
                            {METHOD_LABELS[p.method]}
                            {p.reference && <span style={{ fontWeight: 400, color: 'var(--ink-tertiary)', marginLeft: 8 }}>{p.reference}</span>}
                          </p>
                          {p.notes && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.notes}</p>}
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{recorderName}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--positive)', textAlign: 'right' }}>
                          {formatCurrency(Number(p.amount))}
                        </span>
                        <button onClick={() => deletePayment(p.id)} title="Remove this payment"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                          <Trash2 style={{ width: 13, height: 13 }} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {!isLocked ? (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment terms, thank you note..." rows={3}
                style={{ width: '100%', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, fontSize: 13, color: 'var(--ink-secondary)', outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'} />
            </div>
          ) : invoice.notes ? (
            <div style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Notes</p>
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.6 }}>{invoice.notes}</p>
            </div>
          ) : null}
        </div>
      </div>

      <AnimatePresence>
        {paymentModalDefault && invoice && (
          <RecordPaymentModal
            invoice={invoice}
            defaultMethod={paymentModalDefault.method}
            modalTitle={paymentModalDefault.title}
            onClose={() => setPaymentModalDefault(null)}
            onSubmit={handlePaymentSubmit}
          />
        )}
        {showLinkContact && invoice && workspace && (
          <LinkContactModal
            invoiceId={invoice.id}
            workspaceId={workspace.id}
            onClose={() => setShowLinkContact(false)}
            onLinked={() => {
              // Refetch the invoice (snapshot + contact_id update) and the list.
              // On refetch hasValidContact flips true → the Link button hides and
              // Make-recurring enables, no extra wiring.
              queryClient.invalidateQueries({ queryKey: ['invoice', id] })
              queryClient.invalidateQueries({ queryKey: ['invoices'] })
              setShowLinkContact(false)
            }}
          />
        )}
        {showMakeRecurringModal && invoice && (
          <MakeRecurringModal
            invoice={invoice}
            onClose={() => setShowMakeRecurringModal(false)}
            onCreated={() => {
              // Pop a "View in Recurring" follow-up via toast action so the
              // operator can jump to the new schedule list immediately.
              toast.success('Schedule created', {
                action: { label: 'View', onClick: () => navigate('/app/invoices/recurring') },
              })
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}